import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { getUserRecommendations } from '@atlaskit/smart-user-picker';
import { NLPSearchErrorState, NLPSearchResultFormat } from '../../../ai-answer-dialog/types';
import { fetchAggResponse } from '../../../common/fetch';
import { formatTimeKnowledgeCard, sanitizeAndMapRecentWorkActivity, trimARI } from '../../util';
import { KNOWLEDGE_CARD_PRIMARY_USER_OPERATION_NAME, KNOWLEDGE_CARD_USER_SEARCH_OPERATION_NAME, KnowledgeCardPrimaryUserDataQuery, KnowledgeCardUserSearchQuery } from './gql';
var MAX_USERS = 10;
export var fetchPersonKnowledgeCardData = function fetchPersonKnowledgeCardData(_ref) {
  var personQuery = _ref.personQuery,
    principalUserId = _ref.principalUserId,
    tenantId = _ref.tenantId,
    intl = _ref.intl;
  try {
    var requestParams = {
      context: {
        contextType: 'contributors',
        productKey: 'people',
        principalId: principalUserId,
        siteId: tenantId
      },
      includeGroups: false,
      includeTeams: false,
      includeUsers: true,
      maxNumberOfResults: MAX_USERS,
      performSearchQueryOnly: true,
      query: personQuery
    };
    return getUserRecommendations(requestParams, intl).then(function (results) {
      var mappedResults = results.map(function (result) {
        return {
          id: result.id,
          name: result.name,
          avatarUrl: result.avatarUrl,
          title: result.title
        };
      });
      var finalResult = {
        user_metadata_only_cards: mappedResults
      };
      return {
        nlpSearch: {
          nlpResult: JSON.stringify(finalResult),
          uniqueSources: [],
          disclaimer: null,
          errorState: null,
          format: NLPSearchResultFormat.JSON
        }
      };
    }).catch(function (_error) {
      return Promise.resolve({
        nlpSearch: {
          errorState: NLPSearchErrorState.NetworkError,
          disclaimer: null,
          format: NLPSearchResultFormat.JSON,
          uniqueSources: []
        }
      });
    });
  } catch (error) {
    return Promise.resolve({
      nlpSearch: {
        errorState: NLPSearchErrorState.NetworkError,
        disclaimer: null,
        format: NLPSearchResultFormat.JSON,
        uniqueSources: []
      }
    });
  }
};
export var fetchPersonKnowledgeCardDataWithPostFiltering = function fetchPersonKnowledgeCardDataWithPostFiltering(_ref2) {
  var personQuery = _ref2.personQuery,
    tenantId = _ref2.tenantId,
    intl = _ref2.intl,
    sourceProduct = _ref2.sourceProduct;
  var siteARI = sourceProduct ? "ari:cloud:".concat(sourceProduct, "::site/").concat(tenantId) : '';
  return fetchAggResponse({
    graphQuery: KnowledgeCardUserSearchQuery,
    variables: {
      userQuery: personQuery,
      siteId: siteARI
    },
    operationName: KNOWLEDGE_CARD_USER_SEARCH_OPERATION_NAME
  }).then(function (_ref3) {
    var _data$knowledgeDiscov, _data$knowledgeDiscov2;
    var data = _ref3.data,
      errors = _ref3.errors;
    var users = (data === null || data === void 0 || (_data$knowledgeDiscov = data.knowledgeDiscovery) === null || _data$knowledgeDiscov === void 0 || (_data$knowledgeDiscov = _data$knowledgeDiscov.searchUser) === null || _data$knowledgeDiscov === void 0 ? void 0 : _data$knowledgeDiscov.users) || [];
    var message = data === null || data === void 0 || (_data$knowledgeDiscov2 = data.knowledgeDiscovery) === null || _data$knowledgeDiscov2 === void 0 || (_data$knowledgeDiscov2 = _data$knowledgeDiscov2.searchUser) === null || _data$knowledgeDiscov2 === void 0 ? void 0 : _data$knowledgeDiscov2.message;
    if (errors || message) {
      throw new Error();
    }
    var mappedResults = users.map(function (user) {
      return {
        id: user.id,
        name: user.name,
        avatarUrl: user.avatarUrl || '',
        title: user.title || '',
        zoneInfo: user.zoneInfo || '',
        location: user.location || ''
      };
    });
    var finalResult = {
      user_metadata_only_cards: mappedResults
    };
    return {
      nlpSearch: {
        nlpResult: JSON.stringify(finalResult),
        uniqueSources: [],
        disclaimer: null,
        errorState: null,
        format: NLPSearchResultFormat.JSON
      }
    };
  }).catch(function () {
    return Promise.resolve({
      nlpSearch: {
        errorState: NLPSearchErrorState.NetworkError,
        disclaimer: null,
        format: NLPSearchResultFormat.JSON,
        uniqueSources: []
      }
    });
  });
};
export var fetchPrimaryUserData = function fetchPrimaryUserData(_ref4) {
  var accountId = _ref4.accountId,
    tenantId = _ref4.tenantId,
    requestingUserId = _ref4.requestingUserId,
    activityClient = _ref4.activityClient,
    locale = _ref4.locale;
  var aggPromise = fetchAggResponse({
    graphQuery: KnowledgeCardPrimaryUserDataQuery,
    variables: {
      accountId: accountId,
      accountIdARI: "ari:cloud:identity::user/".concat(accountId),
      tenantId: tenantId,
      requestingUserId: requestingUserId
    },
    operationName: KNOWLEDGE_CARD_PRIMARY_USER_OPERATION_NAME
  });
  var activityPromise = activityClient.fetchActivities([100], {
    limit: 50,
    actors: ["ari:cloud:identity::user/".concat(accountId)]
  }, ['workedOn'], false);
  return Promise.all([aggPromise, activityPromise]).then(function (_ref5) {
    var _aggResponse$data, _aggResponse$data2, _aggResponse$data3, _aggResponse$data4, _aggResponse$data5, _aggResponse$data6, _aggResponse$data7, _aggResponse$data8, _aggResponse$data9;
    var _ref6 = _slicedToArray(_ref5, 2),
      aggResponse = _ref6[0],
      activityResponse = _ref6[1];
    var primaryUserData = {
      account_id: accountId,
      name: ((_aggResponse$data = aggResponse.data) === null || _aggResponse$data === void 0 ? void 0 : _aggResponse$data.user.name) || '',
      job_title: (_aggResponse$data2 = aggResponse.data) === null || _aggResponse$data2 === void 0 || (_aggResponse$data2 = _aggResponse$data2.user.extendedProfile) === null || _aggResponse$data2 === void 0 ? void 0 : _aggResponse$data2.jobTitle,
      profile_url: "/people/".concat(accountId),
      avatar_url: (_aggResponse$data3 = aggResponse.data) === null || _aggResponse$data3 === void 0 ? void 0 : _aggResponse$data3.user.picture,
      email: (_aggResponse$data4 = aggResponse.data) === null || _aggResponse$data4 === void 0 ? void 0 : _aggResponse$data4.user.email,
      location: (_aggResponse$data5 = aggResponse.data) === null || _aggResponse$data5 === void 0 || (_aggResponse$data5 = _aggResponse$data5.user.extendedProfile) === null || _aggResponse$data5 === void 0 ? void 0 : _aggResponse$data5.location,
      department: (_aggResponse$data6 = aggResponse.data) === null || _aggResponse$data6 === void 0 || (_aggResponse$data6 = _aggResponse$data6.user.extendedProfile) === null || _aggResponse$data6 === void 0 ? void 0 : _aggResponse$data6.department,
      organization: (_aggResponse$data7 = aggResponse.data) === null || _aggResponse$data7 === void 0 || (_aggResponse$data7 = _aggResponse$data7.user.extendedProfile) === null || _aggResponse$data7 === void 0 ? void 0 : _aggResponse$data7.organization,
      time: (_aggResponse$data8 = aggResponse.data) !== null && _aggResponse$data8 !== void 0 && _aggResponse$data8.user.zoneinfo ? formatTimeKnowledgeCard({
        locale: locale,
        zoneInfo: aggResponse.data.user.zoneinfo
      }) : undefined,
      collaborators: (_aggResponse$data9 = aggResponse.data) === null || _aggResponse$data9 === void 0 ? void 0 : _aggResponse$data9.smarts.recommendedUser.reduce(function (acc, node) {
        if (trimARI(node.user.id) !== accountId) {
          var _node$user$extendedPr, _node$user$extendedPr2;
          acc.push({
            name: node.user.name,
            picture: node.user.picture,
            title: !!((_node$user$extendedPr = node.user.extendedProfile) !== null && _node$user$extendedPr !== void 0 && _node$user$extendedPr.jobTitle) ? node.user.extendedProfile.jobTitle : null,
            url: !!node.user.id ? "/people/".concat(trimARI(node.user.id)) : null,
            accountId: node.user.id,
            department: !!((_node$user$extendedPr2 = node.user.extendedProfile) !== null && _node$user$extendedPr2 !== void 0 && _node$user$extendedPr2.department) ? node.user.extendedProfile.department : null,
            email: !!node.user.email ? node.user.email : null
          });
        }
        return acc;
      }, []),
      viewAllLink: "/people/".concat(accountId),
      recentActivity: activityResponse.workedOn && activityResponse.workedOn.length > 0 ? sanitizeAndMapRecentWorkActivity(activityResponse.workedOn) : []
    };
    return primaryUserData;
  });
};